import { Flex } from 'antd';
import { darken } from 'polished';
import type * as React from 'react';
import styled from 'styled-components';
import { FlexRow } from '../row/FlexRow.tsx';
import { ConfigurationCardDescriptionPopover } from './ConfigurationCardDescriptionPopover.tsx';

/**
 * Wrapper dell'icona. Esportato perché la `ConfigurationCard` ne ridefinisce le
 * dimensioni nella variante `dense` interpolandolo nel proprio styled.
 */
export const ConfigurationCardIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${props => darken(0.05, props.theme.colorBgLayout)};
  border-radius: 6px;
  color: ${props => props.theme.colorPrimary};
  margin-right: 16px;
  font-size: 20px;
`;

/**
 * Wrapper di titolo + descrizione. Esportato per lo stesso motivo
 * dell'icona: la `ConfigurationCard` lo interpola nelle regole `dense`.
 */
export const ConfigurationCardTitleWrapper = styled(Flex)`
  min-height: 40px;
  justify-content: center;
  .title, .description {
    margin: 0;
    padding: 0;
  }
  .title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
  }
  .description {
    font-size: 15px;
    font-weight: 400;
    line-height: 1.2;
    color: ${props => props.theme.colorTextSecondary};
  }
`;

export interface IConfigurationCardHeaderProps {
  title: React.ReactNode;
  icon: React.ReactNode;
  description: React.ReactNode;
  dense: boolean;
  /**
   * Contenuto opzionale a destra nell'header (es. chip di stato). Reso qui
   * solo nel ramo Card: nel ramo Collapse la card lo passa allo slot nativo
   * `extra`.
   */
  summary?: React.ReactNode;
}

/**
 * Header della `ConfigurationCard`: icona + titolo + descrizione (in `dense`
 * come popover ⓘ accanto al titolo). Usato sia come `title` della Card
 * (pagine) sia come `label` del Collapse (drawer): nel secondo caso l'header
 * antd è il controllo del toggle, quindi il popover ⓘ ferma la propagazione di
 * click e tastiera per non azionare anche il collapse (pattern ufficiale antd
 * per gli elementi interattivi dentro l'header).
 */
export function ConfigurationCardHeader(props: IConfigurationCardHeaderProps) {
  const { title, icon, description, dense, summary } = props;

  return (
    <FlexRow
      left={
        <ConfigurationCardIconWrapper aria-hidden="true">
          {icon}
        </ConfigurationCardIconWrapper>
      }
      right={
        summary != null ? (
          <Flex align="center" justify="flex-end" gap={8} wrap="wrap">
            {summary}
          </Flex>
        ) : undefined
      }
      align="flex-start"
    >
      <ConfigurationCardTitleWrapper vertical gap={6}>
        <div className="title">
          <span className="title-text">{title}</span>
          {dense && description != null && (
            <ConfigurationCardDescriptionPopover description={description} />
          )}
        </div>
        {!dense && <div className="description">{description}</div>}
      </ConfigurationCardTitleWrapper>
    </FlexRow>
  );
}
